<template>
  <v-expansion-panels
    v-model="mixedPanelOpen"
    accordion
    flat
    class="mb-4"
  >
    <v-expansion-panel class="rounded-lg panel-homogenizado">
      <v-expansion-panel-header class="py-2">
        <div class="d-flex align-center">
          <v-icon :color="themeColor" class="mr-2">mdi-tune</v-icon>
          <span class="font-weight-bold text-subtitle-2">
            Panel de Campos Comunes en Lote
          </span>
          <span class="text-caption grey--text ml-3">
            (Aplica categoría, canales, precio o deptos en bloque y
            personaliza casos específicos en la tabla inferior)
          </span>
        </div>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-row dense class="pt-2">
          <!-- 1. Categoría común -->
          <v-col cols="12" md="6">
            <v-card
              outlined
              class="pa-4 rounded-lg h-100 grey lighten-5 d-flex flex-column justify-space-between"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <v-checkbox
                  v-model="options.category.enabled"
                  label="Categoría común para todas"
                  dense
                  hide-details
                  class="font-weight-medium"
                ></v-checkbox>
              </div>
              <v-autocomplete
                :disabled="!options.category.enabled"
                v-model="options.category.targetId"
                :items="categories"
                item-value="id"
                item-text="Nombre"
                label="Seleccionar categoría"
                placeholder="Buscar categoría..."
                outlined
                dense
                clearable
                hide-details
              ></v-autocomplete>
            </v-card>
          </v-col>

          <!-- 2. Canales de venta en lote -->
          <v-col cols="12" md="6">
            <v-card
              outlined
              class="pa-4 rounded-lg h-100 grey lighten-5 d-flex flex-column justify-space-between"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <v-checkbox
                  v-model="options.channels.enabled"
                  label="Canales de venta comunes"
                  dense
                  hide-details
                  class="font-weight-medium"
                ></v-checkbox>
                <v-chip
                  v-if="options.channels.enabled"
                  x-small
                  :color="themeLightColor"
                  :class="`${themeTextClass} font-weight-bold`"
                >
                  {{ mixedChannelsSummary }}
                </v-chip>
              </div>

              <div
                class="d-flex align-center flex-wrap pt-1"
                style="gap: 8px"
              >
                <v-chip
                  v-for="ch in channelConfigs"
                  :key="ch.mixedKey"
                  :disabled="!options.channels.enabled"
                  small
                  :color="
                    options.channels[ch.mixedKey]
                      ? ch.bgClass
                      : 'grey lighten-3'
                  "
                  :class="
                    options.channels[ch.mixedKey]
                      ? `${ch.textClass} font-weight-bold`
                      : 'grey--text text--darken-1'
                  "
                  class="cursor-pointer"
                  @click="
                    options.channels[ch.mixedKey] =
                      !options.channels[ch.mixedKey]
                  "
                >
                  <v-icon
                    left
                    small
                    :color="
                      options.channels[ch.mixedKey]
                        ? ch.activeColor
                        : 'grey'
                    "
                  >
                    {{
                      options.channels[ch.mixedKey]
                        ? 'mdi-check-circle'
                        : 'mdi-close-circle'
                    }}
                  </v-icon>
                  <v-icon
                    left
                    small
                    :color="
                      options.channels[ch.mixedKey]
                        ? ch.activeColor
                        : 'grey'
                    "
                  >
                    {{ ch.icon }}
                  </v-icon>
                  {{ ch.label }}
                </v-chip>
              </div>
            </v-card>
          </v-col>

          <!-- 3. Nombres de recetas en lote -->
          <v-col cols="12" md="6" class="mt-2">
            <v-card
              outlined
              class="pa-4 rounded-lg h-100 grey lighten-5 d-flex flex-column justify-space-between"
            >
              <div>
                <div
                  class="d-flex align-center justify-space-between flex-nowrap mb-2"
                  style="min-height: 32px"
                >
                  <div class="d-flex align-center flex-shrink-1 text-truncate mr-2">
                    <v-checkbox
                      v-model="options.name.enabled"
                      label="Nombres en lote"
                      dense
                      hide-details
                      class="font-weight-medium mr-2 flex-shrink-0"
                    ></v-checkbox>
                    <v-chip
                      v-if="options.name.enabled && mixedNameSummary"
                      x-small
                      :color="themeLightColor"
                      :class="`${themeTextClass} font-weight-bold text-truncate`"
                      style="max-width: 120px"
                    >
                      {{ mixedNameSummary }}
                    </v-chip>
                  </div>

                  <!-- Selector visual de modo de nombre -->
                  <div
                    class="d-flex align-center flex-shrink-0 my-1"
                    style="gap: 4px"
                  >
                    <v-btn
                      x-small
                      :color="
                        options.name.mode === 'prefix_suffix'
                          ? themeColor
                          : 'grey lighten-3'
                      "
                      :class="
                        options.name.mode === 'prefix_suffix'
                          ? 'white--text'
                          : 'grey--text text--darken-2'
                      "
                      depressed
                      class="text-capitalize px-2"
                      :disabled="!options.name.enabled"
                      @click="options.name.mode = 'prefix_suffix'"
                    >
                      <v-icon left x-small>mdi-format-title</v-icon>
                      Prefijo/Sufijo
                    </v-btn>
                    <v-btn
                      x-small
                      :color="
                        options.name.mode === 'replace'
                          ? themeColor
                          : 'grey lighten-3'
                      "
                      :class="
                        options.name.mode === 'replace'
                          ? 'white--text'
                          : 'grey--text text--darken-2'
                      "
                      depressed
                      class="text-capitalize px-2"
                      :disabled="!options.name.enabled"
                      @click="options.name.mode = 'replace'"
                    >
                      <v-icon left x-small>mdi-find-replace</v-icon>
                      Reemplazar
                    </v-btn>
                    <v-btn
                      x-small
                      :color="
                        options.name.mode === 'keep_original'
                          ? themeColor
                          : 'grey lighten-3'
                      "
                      :class="
                        options.name.mode === 'keep_original'
                          ? 'white--text'
                          : 'grey--text text--darken-2'
                      "
                      depressed
                      class="text-capitalize px-2"
                      :disabled="!options.name.enabled"
                      @click="options.name.mode = 'keep_original'"
                    >
                      <v-icon left x-small>mdi-backup-restore</v-icon>
                      Original
                    </v-btn>
                  </div>
                </div>

                <v-row dense align="center" class="mt-1">
                  <!-- CASO 1: Prefijo / Sufijo -->
                  <template v-if="options.name.mode === 'prefix_suffix'">
                    <v-col cols="6">
                      <v-text-field
                        :disabled="!options.name.enabled"
                        v-model="options.name.prefix"
                        label="Prefijo (opcional)"
                        placeholder="Ej: [Promo]"
                        dense
                        outlined
                        hide-details
                        clearable
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        :disabled="!options.name.enabled"
                        v-model="options.name.suffix"
                        label="Sufijo (opcional)"
                        placeholder="Ej: (Copia)"
                        dense
                        outlined
                        hide-details
                        clearable
                      ></v-text-field>
                    </v-col>
                  </template>

                  <!-- CASO 2: Buscar y Reemplazar -->
                  <template v-else-if="options.name.mode === 'replace'">
                    <v-col cols="6">
                      <v-text-field
                        :disabled="!options.name.enabled"
                        v-model="options.name.findText"
                        label="Buscar texto"
                        placeholder="Ej: (Copia)"
                        dense
                        outlined
                        hide-details
                        clearable
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        :disabled="!options.name.enabled"
                        v-model="options.name.replaceText"
                        label="Reemplazar por"
                        placeholder="Ej: 2026"
                        dense
                        outlined
                        hide-details
                        clearable
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" class="pt-1">
                      <v-checkbox
                        :disabled="!options.name.enabled"
                        v-model="options.name.caseSensitive"
                        label="Distinguir mayúsculas y minúsculas"
                        dense
                        hide-details
                        class="caption mt-0"
                      ></v-checkbox>
                    </v-col>
                  </template>

                  <!-- CASO 3: Mantener Original -->
                  <template v-else-if="options.name.mode === 'keep_original'">
                    <v-col cols="12">
                      <div class="pa-2 grey lighten-4 rounded d-flex align-center">
                        <v-icon small color="primary" class="mr-2">mdi-history</v-icon>
                        <span class="caption grey--text text--darken-3">
                          Se restablecerá el nombre original de cada receta en todas las filas seleccionadas.
                        </span>
                      </div>
                    </v-col>
                  </template>
                </v-row>
              </div>

              <!-- Explicación -->
              <v-sheet
                v-if="options.name.enabled"
                outlined
                rounded
                class="mt-2 pa-2 white"
              >
                <span
                  class="caption grey--text text--darken-2 d-flex align-center"
                >
                  <v-icon x-small color="info" class="mr-1"
                    >mdi-information-outline</v-icon
                  >
                  {{ mixedNameExplanation }}
                </span>
              </v-sheet>
            </v-card>
          </v-col>

          <!-- 4. Ajuste de precio en lote -->
          <v-col cols="12" md="6" class="mt-2">
            <v-card
              outlined
              class="pa-4 rounded-lg h-100 grey lighten-5 d-flex flex-column justify-space-between"
            >
              <div>
                <div
                  class="d-flex align-center justify-space-between flex-wrap mb-2"
                >
                  <div class="d-flex align-center">
                    <v-checkbox
                      v-model="options.price.enabled"
                      label="Ajuste de precio en lote"
                      dense
                      hide-details
                      class="font-weight-medium mr-2"
                    ></v-checkbox>
                    <v-chip
                      v-if="options.price.enabled"
                      x-small
                      :color="themeLightColor"
                      :class="`${themeTextClass} font-weight-bold`"
                    >
                      {{ mixedPriceModeLabel }}
                    </v-chip>
                  </div>

                  <!-- Selector visual del tipo de ajuste -->
                  <div
                    class="d-flex align-center my-1"
                    style="gap: 6px"
                  >
                    <v-btn
                      x-small
                      :color="
                        options.price.type === 'percent'
                          ? themeColor
                          : 'grey lighten-3'
                      "
                      :class="
                        options.price.type === 'percent'
                          ? 'white--text'
                          : 'grey--text text--darken-2'
                      "
                      depressed
                      class="text-capitalize"
                      :disabled="!options.price.enabled"
                      @click="options.price.type = 'percent'"
                    >
                      <v-icon left x-small>mdi-percent</v-icon>
                      Porcentual
                    </v-btn>
                    <v-btn
                      x-small
                      :color="
                        options.price.type === 'amount'
                          ? themeColor
                          : 'grey lighten-3'
                      "
                      :class="
                        options.price.type === 'amount'
                          ? 'white--text'
                          : 'grey--text text--darken-2'
                      "
                      depressed
                      class="text-capitalize"
                      :disabled="!options.price.enabled"
                      @click="options.price.type = 'amount'"
                    >
                      <v-icon left x-small>mdi-currency-usd</v-icon>
                      Unidades MXN
                    </v-btn>
                    <v-btn
                      x-small
                      :color="
                        options.price.type === 'fixed'
                          ? themeColor
                          : 'grey lighten-3'
                      "
                      :class="
                        options.price.type === 'fixed'
                          ? 'white--text'
                          : 'grey--text text--darken-2'
                      "
                      depressed
                      class="text-capitalize"
                      :disabled="!options.price.enabled"
                      @click="options.price.type = 'fixed'"
                    >
                      <v-icon left x-small>mdi-tag</v-icon>
                      Precio Fijo
                    </v-btn>
                  </div>
                </div>

                <v-row dense align="center" class="mt-1">
                  <!-- CASO 1: Porcentual -->
                  <template v-if="options.price.type === 'percent'">
                    <v-col cols="6">
                      <v-select
                        :disabled="!options.price.enabled"
                        v-model="options.price.percentType"
                        :items="[
                          { text: '+ Aumento (%)', value: 1 },
                          { text: '- Descuento (%)', value: -1 },
                        ]"
                        dense
                        outlined
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        :disabled="!options.price.enabled"
                        v-model.number="options.price.percentValue"
                        type="number"
                        min="0"
                        max="1000"
                        suffix="%"
                        dense
                        outlined
                        hide-details
                        placeholder="Ej: 10"
                      ></v-text-field>
                    </v-col>
                  </template>

                  <!-- CASO 2: Unidades MXN -->
                  <template v-else-if="options.price.type === 'amount'">
                    <v-col cols="6">
                      <v-select
                        :disabled="!options.price.enabled"
                        v-model="options.price.amountType"
                        :items="[
                          { text: '+ Sumar MXN', value: 1 },
                          { text: '- Restar MXN', value: -1 },
                        ]"
                        dense
                        outlined
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        :disabled="!options.price.enabled"
                        v-model.number="options.price.amountValue"
                        type="number"
                        min="0"
                        prefix="$"
                        suffix="MXN"
                        dense
                        outlined
                        hide-details
                        placeholder="Ej: 20"
                      ></v-text-field>
                    </v-col>
                  </template>

                  <!-- CASO 3: Precio Fijo -->
                  <template v-else-if="options.price.type === 'fixed'">
                    <v-col cols="12">
                      <v-text-field
                        :disabled="!options.price.enabled"
                        v-model.number="options.price.fixedValue"
                        type="number"
                        min="0"
                        prefix="$"
                        suffix="MXN"
                        label="Nuevo precio fijo común"
                        dense
                        outlined
                        hide-details
                        placeholder="Ej: 150.00"
                      ></v-text-field>
                    </v-col>
                  </template>
                </v-row>
              </div>

              <!-- Explicación -->
              <v-sheet
                v-if="options.price.enabled"
                outlined
                rounded
                class="mt-2 pa-2 white"
              >
                <span
                  class="caption grey--text text--darken-2 d-flex align-center"
                >
                  <v-icon x-small color="info" class="mr-1"
                    >mdi-information-outline</v-icon
                  >
                  {{ mixedPriceExplanation }}
                </span>
              </v-sheet>
            </v-card>
          </v-col>

          <!-- 5. Departamentos y Almacenes Comunes -->
          <v-col cols="12" class="mt-2">
            <v-card
              outlined
              class="pa-4 rounded-lg h-100 grey lighten-5 d-flex flex-column justify-space-between"
            >
              <div>
                <div
                  class="d-flex align-center justify-space-between flex-wrap mb-2"
                >
                  <div class="d-flex align-center">
                    <v-checkbox
                      v-model="options.depto.enabled"
                      label="Deptos. y almacenes comunes"
                      dense
                      hide-details
                      class="font-weight-medium mr-2"
                    ></v-checkbox>
                    <v-chip
                      v-if="options.depto.enabled"
                      x-small
                      :color="themeLightColor"
                      :class="`${themeTextClass} font-weight-bold`"
                    >
                      {{ mixedCommonDeptosList.length }} depto(s)
                    </v-chip>
                  </div>
                  <v-checkbox
                    :disabled="!options.depto.enabled"
                    v-model="options.depto.preserveExisting"
                    label="Conservar existentes"
                    dense
                    hide-details
                    class="caption"
                  ></v-checkbox>
                </div>

                <div v-if="options.depto.enabled">
                  <v-row dense align="center" class="mb-2">
                    <v-col cols="12" sm="5">
                      <v-autocomplete
                        v-model="mixedNewDepto.depto"
                        :items="modulos"
                        item-value="value"
                        item-text="text"
                        label="Departamento *"
                        placeholder="Selecciona..."
                        outlined
                        dense
                        hide-details
                        clearable
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="5">
                      <v-autocomplete
                        v-model="mixedNewDepto.origen"
                        :items="origenes"
                        item-value="id"
                        item-text="Nombre"
                        label="Almacén *"
                        placeholder="Selecciona almacén..."
                        clearable
                        outlined
                        dense
                        hide-details
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="2">
                      <v-btn
                        :color="themeColor"
                        dark
                        depressed
                        block
                        :disabled="!mixedNewDepto.depto || !mixedNewDepto.origen"
                        @click="addCommonDepto"
                        title="Agregar departamento"
                      >
                        <v-icon small>mdi-plus</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>

                  <div class="mb-2">
                    <v-sheet
                      v-if="mixedCommonDeptosList.length === 0"
                      outlined
                      rounded
                      class="pa-3 text-center white"
                    >
                      <span class="caption grey--text">
                        Selecciona departamento y almacenes arriba y
                        haz clic en <strong>+</strong>.
                      </span>
                    </v-sheet>
                    <v-card
                      v-else
                      outlined
                      class="rounded overflow-hidden"
                      style="max-height: 180px; overflow-y: auto"
                    >
                      <v-simple-table dense class="white">
                        <template v-slot:default>
                          <thead>
                            <tr class="grey lighten-4">
                              <th style="width: 35%">
                                Departamento
                              </th>
                              <th>Almacén</th>
                              <th
                                class="text-center"
                                style="width: 50px"
                              >
                                Quitar
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="dep in mixedCommonDeptosList"
                              :key="dep.depto"
                            >
                              <td class="font-weight-medium">
                                <div class="d-flex align-center">
                                  <v-icon
                                    small
                                    :color="themeColor"
                                    class="mr-1"
                                    >mdi-domain</v-icon
                                  >
                                  <span class="text-truncate">{{
                                    dep.deptoName
                                  }}</span>
                                </div>
                              </td>
                              <td>
                                <div class="py-1">
                                  <v-autocomplete
                                    :value="
                                      dep.origenes && dep.origenes.length > 0
                                        ? dep.origenes[0]
                                        : null
                                    "
                                    @input="
                                      updateCommonDeptoOrigenes(
                                        dep.depto,
                                        $event ? [$event] : []
                                      )
                                    "
                                    :items="origenes"
                                    item-value="id"
                                    item-text="Nombre"
                                    placeholder="Seleccionar almacén..."
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                  ></v-autocomplete>
                                </div>
                              </td>
                              <td class="text-center">
                                <v-btn
                                  icon
                                  small
                                  color="red"
                                  @click="removeCommonDepto(dep.depto)"
                                  title="Quitar"
                                >
                                  <v-icon small>mdi-delete-outline</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-card>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Botón Aplicar Reglas Mixtas -->
        <div class="d-flex justify-end mt-3">
          <v-btn
            :color="themeColor"
            dark
            depressed
            @click="$emit('apply')"
            :disabled="
              !options.category.enabled &&
              !options.price.enabled &&
              !options.depto.enabled &&
              !options.channels.enabled &&
              !options.name.enabled
            "
          >
            <v-icon left small>mdi-check-all</v-icon>
            Previsualizar
          </v-btn>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { getDeptoName } from "../../../../clases/Restaurante/ReplicadorRecetas.js";

export default {
  name: "PanelAjustesLote",
  props: {
    options: {
      type: Object,
      required: true,
    },
    commonCCs: {
      type: Object,
      default: () => ({}),
    },
    categories: {
      type: Array,
      default: () => [],
    },
    modulos: {
      type: Array,
      default: () => [],
    },
    origenes: {
      type: Array,
      default: () => [],
    },
    channelConfigs: {
      type: Array,
      default: () => [],
    },
    modulosMap: {
      type: Map,
      default: () => new Map(),
    },
    themeColor: {
      type: String,
      default: "primary",
    },
    themeLightColor: {
      type: String,
      default: "blue lighten-5",
    },
    themeTextClass: {
      type: String,
      default: "primary--text",
    },
  },
  data() {
    return {
      mixedPanelOpen: 0,
      mixedNewDepto: {
        depto: null,
        origen: null,
      },
    };
  },
  computed: {
    mixedNameSummary() {
      const mode = this.options.name?.mode || "prefix_suffix";
      if (mode === "prefix_suffix") {
        const p = this.options.name?.prefix ? `"${this.options.name.prefix}"` : "";
        const s = this.options.name?.suffix ? `"${this.options.name.suffix}"` : "";
        if (p && s) return `${p} + ${s}`;
        if (p) return `Pref: ${p}`;
        if (s) return `Suf: ${s}`;
        return "";
      }
      if (mode === "replace") {
        if (this.options.name?.findText) {
          return `"${this.options.name.findText}"`;
        }
        return "";
      }
      return "";
    },
    mixedNameExplanation() {
      const mode = this.options.name?.mode || "prefix_suffix";
      const sample = "Hamburguesa Clásica";
      if (mode === "prefix_suffix") {
        const pRaw = this.options.name?.prefix ? String(this.options.name.prefix).trim() : "";
        const sRaw = this.options.name?.suffix ? String(this.options.name.suffix).trim() : "";
        const p = pRaw ? `${pRaw} ` : "";
        const s = sRaw ? ` ${sRaw}` : "";
        const result = `${p}${sample}${s}`.trim();
        return `Ejemplo: "${sample}" -> "${result}".`;
      }
      if (mode === "replace") {
        const find = this.options.name?.findText;
        const rep = this.options.name?.replaceText || "";
        if (!find) {
          return "Escribe el texto que deseas buscar y el texto de reemplazo.";
        }
        return `Se buscará "${find}" y se reemplazará por "${rep}" en el nombre de cada receta.`;
      }
      if (mode === "keep_original") {
        return "Se restaurará el nombre original del producto para todas las recetas seleccionadas.";
      }
      return "";
    },
    mixedChannelsSummary() {
      const active = [];
      if (this.options.channels?.pdv) active.push("PDV");
      if (this.options.channels?.web) active.push("Web");
      if (this.options.channels?.cmd) active.push("Móvil");
      return active.length > 0 ? active.join(", ") : "Ninguno activo";
    },
    mixedPriceModeLabel() {
      const mode = this.options.price?.type || "percent";
      if (mode === "percent") {
        const sign = Number(this.options.price?.percentType) > 0 ? "+" : "-";
        return `Porcentual: ${sign}${this.options.price?.percentValue || 0}%`;
      }
      if (mode === "amount") {
        const sign = Number(this.options.price?.amountType) > 0 ? "+" : "-";
        return `Unidades: ${sign}$${this.options.price?.amountValue || 0} MXN`;
      }
      if (mode === "fixed") {
        return `Fijo: $${(Number(this.options.price?.fixedValue) || 0).toFixed(2)} MXN`;
      }
      return "";
    },
    mixedPriceExplanation() {
      const mode = this.options.price?.type || "percent";
      if (mode === "percent") {
        const isUp = Number(this.options.price?.percentType) > 0;
        const val = Number(this.options.price?.percentValue) || 0;
        const sampleBase = 100;
        const sampleResult = isUp
          ? (sampleBase * (1 + val / 100)).toFixed(2)
          : Math.max(0, sampleBase * (1 - val / 100)).toFixed(2);
        return isUp
          ? `Aumento de +${val}% sobre el precio base de cada receta (ej. $100.00 pasará a $${sampleResult}).`
          : `Reducción/Descuento de -${val}% sobre el precio base de cada receta (ej. $100.00 pasará a $${sampleResult}).`;
      }
      if (mode === "amount") {
        const isUp = Number(this.options.price?.amountType) > 0;
        const val = Number(this.options.price?.amountValue) || 0;
        const sampleBase = 100;
        const sampleResult = isUp
          ? (sampleBase + val).toFixed(2)
          : Math.max(0, sampleBase - val).toFixed(2);
        return isUp
          ? `Se sumarán +$${val.toFixed(2)} MXN al precio original de cada receta (ej. $100.00 pasará a $${sampleResult} MXN).`
          : `Se restarán -$${val.toFixed(2)} MXN al precio original de cada receta (ej. $100.00 pasará a $${sampleResult} MXN).`;
      }
      if (mode === "fixed") {
        const val = Number(this.options.price?.fixedValue) || 0;
        return `Precio Fijo: Todas las recetas tendrán exactamente $${val.toFixed(2)} MXN, sin importar su precio original.`;
      }
      return "";
    },
    mixedCommonDeptosList() {
      if (!this.commonCCs || typeof this.commonCCs !== "object") return [];
      return Object.entries(this.commonCCs).map(([depto, origenes]) => ({
        depto,
        deptoName: getDeptoName(depto, this.modulosMap, this.modulos),
        origenes: Array.isArray(origenes)
          ? origenes
          : origenes
          ? [origenes]
          : [],
      }));
    },
  },
  methods: {
    addCommonDepto() {
      if (!this.mixedNewDepto.depto || !this.mixedNewDepto.origen) return;
      const dKey = String(this.mixedNewDepto.depto).trim();
      const origenes = [this.mixedNewDepto.origen];

      this.$set(this.commonCCs, dKey, origenes);
      this.mixedNewDepto = { depto: null, origen: null };
    },
    removeCommonDepto(deptoKey) {
      if (!this.commonCCs) return;
      this.$delete(this.commonCCs, deptoKey);
    },
    updateCommonDeptoOrigenes(deptoKey, newOrigenes) {
      const arr = Array.isArray(newOrigenes)
        ? [...newOrigenes]
        : newOrigenes
        ? [newOrigenes]
        : [];
      this.$set(this.commonCCs, deptoKey, arr);
    },
  },
};
</script>

<style scoped>
.panel-homogenizado {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  width: 100% !important;
}
.panel-homogenizado::before {
  box-shadow: none !important;
}
.panel-homogenizado >>> .v-expansion-panel-header {
  min-height: 48px !important;
  padding: 8px 16px !important;
}
.panel-homogenizado >>> .v-expansion-panel-content__wrap {
  padding: 0 16px 16px 16px !important;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
